<template>
  <div>
    <div class="bg-blue-900 p-4 lg:p-12">
      <div class="max-w-5xl mx-auto mb-12">
        <div class="flex flex-col md:flex-row md:items-center">
          <h4 class="text-white font-bold">Providers</h4>
        </div>
      </div>
    </div>
    <div class="px-4 pb-4 lg:px-12 lg:pb-12 -mt-12">
      <div class="max-w-5xl mx-auto">
        <div class="space-y-16">
          <VList :meta="meta">
            <template #before>
              <div class="hidden lg:flex font-medium text-sm text-gray-600 mb-4 pr-2">
                <div class="flex-1 px-4">Name</div>
                <div class="w-32 px-4">Code</div>
                <div class="w-32 px-4 text-right">Balance</div>
                <div class="w-48 px-4 mr-12 text-right">Date Created</div>
              </div>
            </template>

            <template v-if="isLoading" #default>
              <div class="odd:bg-gray-100 border border-gray-200 rounded p-4 text-center">
                <Loader />
              </div>
            </template>

            <template v-else-if="isRejected" #default>
              <div
                class="odd:bg-red-100 border border-red-200 rounded p-4 text-red-600 text-center"
              >
                Something went wrong. Please try again later.
              </div>
            </template>

            <template v-else-if="providers.length" #default>
              <template v-for="provider in providers">
                <VListLine
                  :key="`providers_${provider.id}`"
                  :provider="provider"
                  :disabled="isLoading"
                  @provider:view="viewProvider"
                />
              </template>
            </template>

            <template v-else #default>
              <div class="odd:bg-gray-100 border border-gray-200 rounded p-4 text-center">
                No results found
              </div>
            </template>
          </VList>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex';
import { middleware } from '@/routes/guards';
import store from '@/store';

import VList from '@/components/Common/VList.vue';
import VListLine from '@/components/AccountProviders/VAccountListLine.vue';

const STATUS_IDLE = 'idle';
const STATUS_PENDING = 'pending';
const STATUS_RESOLVED = 'resolved';
const STATUS_REJECTED = 'rejected';

export default {
  components: {
    VList,
    VListLine,
  },
  props: {
    accountId: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    status: STATUS_IDLE,
  }),
  computed: {
    ...mapState('ui/accountProviders', ['meta']),
    providers() {
      return this.$store.getters['ui/accountProviders/providersByAccount'](this.accountId);
    },
    isLoading() {
      return this.status === STATUS_PENDING;
    },
    isResolved() {
      return this.status === STATUS_RESOLVED;
    },
    isRejected() {
      return this.status === STATUS_REJECTED;
    },
  },
  watch: {
    $route: 'fetchData',
  },
  destroyed() {
    this.setIds([]);
    this.setMeta(null);
    this.setLinks(null);
  },
  beforeRouteEnter(to, from, next) {
    const check = middleware('account-permission:read providers');

    check(to, from, async (args) => {
      if (typeof args !== 'undefined') {
        return next(...args);
      }

      const { accountId } = to.params;
      const { query: params } = to;

      try {
        await store.dispatch('ui/accountProviders/load', { accountId, params });
        return next();
      } catch (error) {
        return next(false);
      }
    });
  },
  methods: {
    ...mapActions('ui/accountProviders', {
      loadAccountProviders: 'load',
    }),
    ...mapMutations('ui/accountProviders', ['setIds', 'setMeta', 'setLinks']),
    async fetchData() {
      const { accountId } = this;
      const { query: params } = this.$route;
      this.status = STATUS_PENDING;
      try {
        await this.loadAccountProviders({ accountId, params });
        this.status = STATUS_RESOLVED;
      } catch (error) {
        this.status = STATUS_REJECTED;
      }
    },
    viewProvider(provider) {
      this.$router.push({
        name: 'accounts.accountId.providers.providerUuid',
        params: {
          accountId: this.accountId,
          providerUuid: provider.uuid,
        },
      });
    },
  },
};
</script>
